import React, { useMemo, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Bar, BarChart, CartesianGrid, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { EmploymentSupport } from './types';
import { buildEmploymentSupportTrend, getEmploymentSupportTypes, hasEmploymentSupportData } from './employmentSupport';
import { ModernAccordion, ModernAccordionSummary, ModernAccordionDetails } from './CompanyDetailsAccordion';
import { getTimeSeriesTooltipStyles, TIME_SERIES_CHART_HEIGHT, TIME_SERIES_CHART_MARGIN, TIME_SERIES_COLORS, TIME_SERIES_TEXT_STYLE } from '../../components/timeSeriesChartStyles';
import { formatNumber } from '../../i18n/formatters';

const COLORS = Object.values(TIME_SERIES_COLORS);

export default function EmploymentSupportCard({ data }: { data?: EmploymentSupport | null }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const tooltipStyles = getTimeSeriesTooltipStyles(theme);
  const [period, setPeriod] = useState('12');
  const [supportType, setSupportType] = useState('');
  const [showTable, setShowTable] = useState(false);
  const [chartWidth, setChartWidth] = useState(0);
  const companyTypes = useMemo(() => data ? getEmploymentSupportTypes(data) : [], [data]);
  const selectedType = companyTypes.includes(supportType) ? supportType : '';
  const trend = useMemo(() => data ? buildEmploymentSupportTrend(data, period === 'all', selectedType) : null, [data, period, selectedType]);
  const allTypes = useMemo(() => Array.from(new Set([...(data?.coverage.flatMap(c => c.supportTypes) ?? []), ...companyTypes])).sort(), [data, companyTypes]);
  const currency = new Intl.NumberFormat(i18n.language?.startsWith('sv') ? 'sv-SE' : 'en-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 2 });
  if (!hasEmploymentSupportData(data) || !trend) return null;

  return (
    <ModernAccordion defaultExpanded>
      <ModernAccordionSummary expandIcon={<ExpandMore />}>
        <Typography>{t('employmentSupport.title')}</Typography>
      </ModernAccordionSummary>
      <ModernAccordionDetails>
          <Typography component="output" aria-label={t('employmentSupport.total')} sx={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600 }}>{currency.format(trend.netAmount)}</Typography>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1.5 }}>{t('employmentSupport.total')}</Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
            <FormControl size="small" sx={{ minWidth: 130, flexGrow: 1 }}>
              <InputLabel id="employment-period-label">{t('employmentSupport.period')}</InputLabel>
              <Select labelId="employment-period-label" label={t('employmentSupport.period')} value={period} onChange={event => setPeriod(event.target.value)}>
                <MenuItem value="12">{t('employmentSupport.last12')}</MenuItem>
                <MenuItem value="all">{t('employmentSupport.allHistory')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140, flexGrow: 1, maxWidth: '100%' }}>
              <InputLabel id="employment-type-label" shrink>{t('employmentSupport.type')}</InputLabel>
              <Select displayEmpty labelId="employment-type-label" label={t('employmentSupport.type')} value={selectedType} onChange={event => setSupportType(event.target.value)}>
                <MenuItem value="">{t('employmentSupport.allTypes')}</MenuItem>
                {companyTypes.map(type => <MenuItem key={type} value={type} sx={{ whiteSpace: 'normal' }}>{type}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box minWidth={0} role="region" aria-label={t('employmentSupport.title')}>
            <ResponsiveContainer width="100%" height={TIME_SERIES_CHART_HEIGHT} onResize={setChartWidth}>
              <BarChart data={trend.rows} accessibilityLayer stackOffset="sign" margin={TIME_SERIES_CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" style={TIME_SERIES_TEXT_STYLE} minTickGap={20} />
                <YAxis style={TIME_SERIES_TEXT_STYLE} tickFormatter={value => formatNumber(value, i18n.resolvedLanguage)} />
                <Tooltip
                  {...tooltipStyles}
                  formatter={(value: number) => currency.format(value)}
                  position={chartWidth > 0 && chartWidth < 480 ? { x: 0 } : undefined}
                  contentStyle={{ ...tooltipStyles.contentStyle, whiteSpace: 'nowrap', overflowX: 'auto' }}
                  itemStyle={{ ...tooltipStyles.itemStyle, whiteSpace: 'nowrap', padding: '2px 0' }}
                  wrapperStyle={{ width: chartWidth > 0 && chartWidth < 480 ? chartWidth : undefined, maxWidth: '100%', pointerEvents: 'auto' }}
                />
                <Legend iconType="square" wrapperStyle={TIME_SERIES_TEXT_STYLE} />
                <ReferenceLine y={0} stroke="#666" />
                {trend.types.map((type, index) => <Bar key={type} dataKey={`type${index}`} name={type} stackId="payments" isAnimationActive={false} fill={COLORS[allTypes.indexOf(type) % COLORS.length]} />)}
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>{t('employmentSupport.note')}</Typography>
          <Button size="small" onClick={() => setShowTable(value => !value)} aria-expanded={showTable}>{t(showTable ? 'employmentSupport.hideTable' : 'employmentSupport.showTable')}</Button>
          {showTable && <TableContainer sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader aria-label={t('employmentSupport.title')}>
              <TableHead><TableRow><TableCell>{t('employmentSupport.month')}</TableCell>{trend.types.map(type => <TableCell key={type} align="right">{type}</TableCell>)}</TableRow></TableHead>
              <TableBody>{trend.rows.map(row => <TableRow key={row.month}><TableCell component="th" scope="row">{row.month}</TableCell>{trend.types.map((type, index) => <TableCell key={type} align="right">{currency.format(row[`type${index}`] as number)}</TableCell>)}</TableRow>)}</TableBody>
            </Table>
          </TableContainer>}
      </ModernAccordionDetails>
    </ModernAccordion>
  );
}
